/**
 * "signed in 40 minutes ago" — the whole of what the single-session prompt is allowed to say
 * (docs/SINGLE-SESSION-PLAN.md D-3).
 *
 * ── Why this is its own file with its own tests ───────────────────────────────────────
 * Because the interesting part is not the wording, it is the clock. `startedAt` comes from the
 * SERVER's clock and is rendered against the BROWSER's, and those disagree — a laptop minutes fast
 * turns a session that began ten seconds ago into one that begins "in two minutes". A prompt that
 * says the future is a prompt nobody believes, and it would arrive silently, on one user's machine,
 * for a reason nobody could reproduce.
 *
 * So anything at or before now, and anything under a minute, is "just now". Skew in the other
 * direction is harmless: it reads as slightly older than it is.
 *
 * ── Why Intl rather than a table of strings ───────────────────────────────────────────
 * Pluralisation. "1 minutes ago" is the kind of thing that ships.
 */

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Chooses the unit a person would use, then lets Intl phrase it. Not a precise duration —
 * "3 hours ago" for two hours and fifty minutes is the answer somebody wants when deciding whether
 * the other session is theirs.
 */
export function sessionAgeLabel(startedAt: string, now: Date = new Date()): string {
  const started = Date.parse(startedAt);
  // An unparseable timestamp must not render "NaN minutes ago"; the prompt still works without the
  // age, because the sentence that matters is "you are signed in somewhere else".
  if (Number.isNaN(started)) return 'a moment ago';

  const elapsed = now.getTime() - started;
  if (elapsed < MINUTE) return 'just now';

  const format = new Intl.RelativeTimeFormat(undefined, { numeric: 'always' });

  if (elapsed < HOUR) return format.format(-Math.floor(elapsed / MINUTE), 'minute');
  if (elapsed < DAY) return format.format(-Math.floor(elapsed / HOUR), 'hour');
  return format.format(-Math.floor(elapsed / DAY), 'day');
}
